import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent } from '@/components/feature/solution/theme';

interface LayerGroup {
  icon: string;
  title: string;
  items: string[];
}

interface LayerDevice {
  icon: string;
  label: string;
}

interface ArchLayer {
  key: string;
  icon: string;
  name: string;
  desc: string;
  groups?: LayerGroup[];
  systems?: string[];
  modules?: string[];
  devices?: LayerDevice[];
}

interface ConnectNode {
  icon: string;
  label: string;
  desc: string;
}

interface ParkingArchitectureProps {
  data: {
    eyebrow: string;
    title: string;
    subtitle: string;
    layers: ArchLayer[];
    connect: { title: string; desc: string; nodes: ConnectNode[] };
  };
}

function Connector() {
  return (
    <div className="flex flex-col items-center py-2">
      <span className="w-px h-4 bg-secondary-300"></span>
      <i className="ri-arrow-down-line text-secondary-500 text-lg leading-none"></i>
      <span className="w-px h-4 bg-secondary-300"></span>
    </div>
  );
}

export default function ParkingArchitecture({ data }: ParkingArchitectureProps) {
  const a = accent('gold');
  const [active, setActive] = useState('platform');

  const layerBody = (layer: ArchLayer) => {
    if (layer.groups) {
      return (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {layer.groups.map((g) => (
            <div key={g.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2.5">
                <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${a.iconBox}`}>
                  <i className={`${g.icon} text-lg`}></i>
                </span>
                <h4 className="font-heading font-bold text-foreground-950 text-sm">{g.title}</h4>
              </div>
              <div className="mt-3 flex flex-col gap-1.5">
                {g.items.map((it) => (
                  <span key={it} className="text-xs text-foreground-600 whitespace-nowrap">
                    {it}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (layer.systems) {
      return (
        <div className="mt-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {layer.systems.map((s) => (
              <div
                key={s}
                className="rounded-xl bg-secondary-500/15 border border-secondary-500/30 px-4 py-3 text-center text-sm font-medium text-secondary-200 whitespace-nowrap"
              >
                {s}
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {layer.modules?.map((m) => (
              <span
                key={m}
                className="px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/15 text-xs text-foreground-300 whitespace-nowrap"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {layer.devices?.map((d) => (
          <div
            key={d.label}
            className="rounded-xl border border-background-200 bg-background-50 py-4 flex flex-col items-center gap-2 text-center"
          >
            <i className={`${d.icon} text-2xl ${a.text}`}></i>
            <span className="text-xs text-foreground-700 leading-snug">{d.label}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <section id="architecture" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50 scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />

        {/* 系统拓扑图 */}
        <Reveal delay={80}>
          <figure className="mt-10 rounded-2xl border border-background-200 bg-background-100 overflow-hidden">
            <img
              src="https://static.readdy.ai/image/4482546e014370507f3d90ff31782979/71ffd4343689884256399df9110f5ef3.png"
              alt="自由流停车系统拓扑图"
              title="自由流停车系统拓扑图 - 端边云协同架构"
              className="w-full h-auto object-contain"
            />
          </figure>
        </Reveal>

        <div className={`mt-12 grid gap-6 lg:gap-8 items-start ${data.layers.length > 0 ? 'grid-cols-1 lg:grid-cols-[1.6fr_1fr]' : 'grid-cols-1'}`}>
          {/* 三层架构 */}
          {data.layers.length > 0 && (
            <Reveal>
              <div className="flex flex-col">
                {data.layers.map((layer, idx) => {
                  const isPlatform = layer.key === 'platform';
                  const isActive = active === layer.key;
                  return (
                    <div key={layer.key}>
                      <button
                        type="button"
                        onClick={() => setActive(layer.key)}
                        className={`w-full text-left rounded-2xl p-5 md:p-6 transition-all duration-300 cursor-pointer ${
                          isPlatform
                            ? 'bg-foreground-950 border border-foreground-800'
                            : 'bg-background-100 border border-background-200'
                        } ${isActive ? 'ring-2 ring-secondary-400/70' : 'hover:border-secondary-300'}`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${
                              isPlatform ? 'bg-secondary-500/20 text-secondary-300' : a.iconBox
                            }`}
                          >
                            <i className={`${layer.icon} text-xl`}></i>
                          </span>
                          <div className="min-w-0">
                            <div
                              className={`font-heading font-bold text-lg leading-tight ${
                                isPlatform ? 'text-background-50' : 'text-foreground-950'
                              }`}
                            >
                              {layer.name}
                            </div>
                            <div className={`text-xs mt-0.5 ${isPlatform ? 'text-foreground-300' : 'text-foreground-500'}`}>
                              {layer.desc}
                            </div>
                          </div>
                        </div>
                        {layerBody(layer)}
                      </button>
                      {idx < data.layers.length - 1 && <Connector />}
                    </div>
                  );
                })}
              </div>
            </Reveal>
          )}

        </div>
      </div>
    </section>
  );
}